import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Form,
    Row,
    Col,
    Alert
} from 'react-bootstrap';
import IrmPeriodOverlapModal from './IrmPeriodOverlapModal';
import instance from '../../services/baseService';
import useSidebar from '../../hooks/useSidebar';
import { showLoader } from "../../slices/commonReducer";
import { useDispatch } from "react-redux";

const PeriodCreationModal = ({ show, onHide, clientId, reFetch }) => {

    const formatDateLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getInitialPeriodDates = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0 = Jan

        const startYear = currentMonth < 3 ? currentYear - 1 : currentYear;

        const startDateObj = new Date(startYear, 3, 1); // April 1 (local)
        const endDateObj = new Date(startDateObj);
        endDateObj.setMonth(endDateObj.getMonth() + 1);

        return {
            startDate: formatDateLocal(startDateObj),
            endDate: formatDateLocal(endDateObj),
        };
    };
    // State management
    const { startDate: initialStartDate, endDate: initialEndDate } = getInitialPeriodDates();
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showOverlapModal, setShowOverlapModal] = useState(false);
    const [overlapData, setOverlapData] = useState(null);
    const [forceCreate, setForceCreate] = useState(false);
    const { collapse, expand, isSidebarCollapsed } = useSidebar();
    const dispatch = useDispatch();
    const startDateRef = React.useRef(null);
    const endDateRef = React.useRef(null);

    // Validate dates
    const isMoreThanOneMonth = (start, end) => {
        const startDateObj = new Date(start);
        const endDateObj = new Date(end);

        const oneMonthLater = new Date(startDateObj);
        oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

        return endDateObj > oneMonthLater;
    };

    const validateDates = () => {
        const newErrors = {};

        if (!startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            newErrors.endDate = 'End date must be after start date';
        }

        if (startDate && endDate && isMoreThanOneMonth(startDate, endDate)) {
            newErrors.endDate = 'Date range cannot exceed 1 month';
        }

        return newErrors;
    };

    const checkPeriodOverlap = async () => {
        try {
            const response = await instance.post(
                `/api/v1/ebrc/irm/period/check-overlap/${clientId}`,
                {
                    start_date: startDate,
                    end_date: endDate
                }
            );

            if (response.data.type === 'warning' && response.data.data.has_overlap) {
                // Overlap detected - show confirmation modal
                setOverlapData(response.data.data);
                setShowOverlapModal(true);
                return false; // Don't proceed with creation
            }

            // No overlap - proceed with creation
            return true;

        } catch (error) {
            console.error('Error checking overlap:', error);
            setErrors({ submit: 'Failed to check period overlap. Please try again.' });
            return false;
        }
    };

    const createPeriod = async (forceCreate = false) => {
        try {
            setIsLoading(true);
            setErrors({});

            // Create FormData for API request
            const formData = new FormData();
            formData.append('start_date', startDate);
            formData.append('end_date', endDate);
            formData.append('force_create', forceCreate);  // Pass force_create flag

            // Make API call
            const response = await instance.post(
                `/api/v1/ebrc/irm/period/create/${clientId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            // Handle response based on status
            if (response.data && response.data.type === 'success') {
                dispatch(showLoader('Running automation task...'));
                const { data } = response.data;

                setSuccess('Automation Started');

                // Wait a moment to show success message
                await new Promise(resolve => setTimeout(resolve, 2500));
                await reFetch();
                resetForm();
                onHide(true, data);

            } else {
                setErrors({ submit: response.data?.message || 'Automation failed. Please try again.' });
            }

        } catch (error) {
            console.error('Automation error:', error);
            setErrors({ submit: error.response?.data?.message || 'Automation failed. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle form submission with API integration
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setSuccess('');

        const dateErrors = validateDates();
        const newErrors = { ...dateErrors };

        if (!clientId) {
            newErrors.submit = 'Client ID is required for upload';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);

        try {
            // Check for overlap first
            const canProceed = await checkPeriodOverlap();

            if (!canProceed) {
                // Overlap detected - modal will be shown by checkPeriodOverlap
                setIsLoading(false);
                return;
            }

            // No overlap - proceed with creation
            await createPeriod(false);

        } catch (error) {
            console.error('Automation error:', error);
            setErrors({ submit: 'Automation failed. Please try again.' });
            setIsLoading(false);
        }
    };

    const handleOverlapConfirm = async () => {
        setShowOverlapModal(false);
        await createPeriod(true);
    };

    const handleOverlapCancel = () => {
        setShowOverlapModal(false);
        setOverlapData(null);
        setIsLoading(false);
    };

    // Reset form - Modified for single file
    const resetForm = () => {
        const { startDate, endDate } = getInitialPeriodDates();
        setStartDate(startDate);
        setEndDate(endDate);
        // setFile(null);
        setErrors({});
        setSuccess('');
        setForceCreate(false);  // ADD THIS LINE
        setOverlapData(null);
    };

    // Handle modal close
    const handleClose = () => {
        if (!isLoading) {
            resetForm();
            onHide(false); // Pass false to indicate no upload
            expand();
        }
    };

    // Clear states when modal is closed
    useEffect(() => {
        if (!show) {
            // Reset form when modal is hidden
            resetForm();
        }
    }, [show]);


    return (
        <>
            <Modal
                show={show}
                onHide={handleClose}
                size="lg"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-0 d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1 text-start">
                        <Modal.Title className="fw-bold">Automate E-BRC</Modal.Title>
                        <p className="text-muted mb-0">Select Start date and End date to fetch IRM.</p>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-3">
                    <Form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <h5 className="fw-semibold mb-3">Select IRM period</h5>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-medium">Start Date</Form.Label>
                                        <Form.Control
                                            ref={startDateRef}
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => {
                                                setStartDate(e.target.value);
                                                // Clear start date error when user types
                                                if (errors.startDate) {
                                                    setErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.startDate;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            onClick={() => startDateRef.current?.showPicker()}
                                            isInvalid={!!errors.startDate}
                                            disabled={isLoading}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.startDate}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="fw-medium">End Date</Form.Label>
                                        <Form.Control
                                            ref={endDateRef}
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => {
                                                setEndDate(e.target.value);
                                                // Clear end date error when user types
                                                if (errors.endDate) {
                                                    setErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.endDate;
                                                        return newErrors;
                                                    });
                                                }
                                            }}
                                            onClick={() => endDateRef.current?.showPicker()}
                                            isInvalid={!!errors.endDate}
                                            disabled={isLoading}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.endDate}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Success Message */}
                        {success && (
                            <Alert variant="success" className="py-2 mb-3">
                                {success}
                            </Alert>
                        )}

                        {/* Error Messages */}
                        {errors.submit && (
                            <Alert variant="danger" className="py-2 mb-3">
                                {errors.submit}
                            </Alert>
                        )}
                    </Form>
                </Modal.Body>

                <Modal.Footer className='border-0'>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        id='generate_section_id'
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isLoading || !startDate || !endDate}
                        className="d-flex align-items-center"
                    >
                        {isLoading ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                Starting...
                            </>
                        ) : (
                            'Start Fetching'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            <IrmPeriodOverlapModal
                show={showOverlapModal}
                onHide={handleOverlapCancel}
                overlapData={overlapData}
                onConfirm={handleOverlapConfirm}
                isLoading={isLoading}
            />
        </>
    );
};

export default PeriodCreationModal;